


import React, { useState, useRef, useEffect } from "react";
import styles from "./StopWatch.module.css";
import SubmitButton from "../submitButton/SubmitButton";

const CountdownModal = () => {
  const [step, setStep] = useState(1);

  const [hours, setHours] = useState("00");
  const [minutes, setMinutes] = useState("00");
  const [seconds, setSeconds] = useState("00");
  const [remaining, setRemaining] = useState(0);
  const [total, setTotal] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);
  const pad = (val) => String(val).padStart(2, "0");

  const getTotalSeconds = () => {
    const h = Number(hours) || 0;
    const m = Number(minutes) || 0;
    const s = Number(seconds) || 0;
    return h * 3600 + m * 60 + s;
  };

  const formatTime = (time) => {
    const h = pad(Math.floor(time / 3600));
    const m = pad(Math.floor((time % 3600) / 60));
    const s = pad(time % 60);
    return `${h}:${m}:${s}`;
  };

  const handleChange = (value, type) => {
    let v = value.replace(/\D/g, "").slice(0, 2);
    if (type === "hours" && v > 23) v = "23";
    if ((type === "minutes" || type === "seconds") && v > 59) v = "59";

    if (type === "hours") {
      setHours(pad(v));
      if (v.length === 2) minutesRef.current.focus();
    }
    if (type === "minutes") {
      setMinutes(pad(v));
      if (v.length === 2) secondsRef.current.focus();
    }
    if (type === "seconds") setSeconds(pad(v));
  };

  const startCountdown = () => {
    const totalSec = getTotalSeconds();
    if (totalSec <= 0) return;
    setTotal(totalSec);
    setRemaining(totalSec);
    setStep(2);
    setIsRunning(true);
  };

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setIsRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  const stop = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
  };

  const resume = () => {
    if (remaining > 0) setIsRunning(true);
  };

  const reset = () => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setStep(1);
    setRemaining(0);
    setHours("00");
    setMinutes("00");
    setSeconds("00");
    setFocusedInput(null);
  };

  const progress = total > 0 ? (remaining / total) * 100 : 100;

  const handleAddDigit = (numStr) => {
    if (focusedInput) {
      if (focusedInput === "hours") {
        let val = (hours + numStr).slice(-2);
        if (val > 23) val = "23";
        setHours(pad(val));
        if (val.length === 2) minutesRef.current.focus();
      } else if (focusedInput === "minutes") {
        let val = (minutes + numStr).slice(-2);
        if (val > 59) val = "59";
        setMinutes(pad(val));
        if (val.length === 2) secondsRef.current.focus();
      } else if (focusedInput === "seconds") {
        let val = (seconds + numStr).slice(-2);
        if (val > 59) val = "59";
        setSeconds(pad(val));
      }
    } else {

      const combined = hours + minutes + seconds + numStr;
      const newVal = combined.slice(-6);
      setHours(pad(newVal.slice(0, 2)));
      setMinutes(pad(newVal.slice(2, 4)));
      setSeconds(pad(newVal.slice(4, 6)));
    }
  };

  const handleDelete = () => {
    if (focusedInput) {
      if (focusedInput === "hours") setHours(hours.slice(0, -1).padStart(2, "0"));
      if (focusedInput === "minutes") setMinutes(minutes.slice(0, -1).padStart(2, "0"));
      if (focusedInput === "seconds") setSeconds(seconds.slice(0, -1).padStart(2, "0"));
    } else {
      const combined = (hours + minutes + seconds).slice(0, -1).padStart(6, "0");
      setHours(combined.slice(0, 2));
      setMinutes(combined.slice(2, 4));
      setSeconds(combined.slice(4, 6));
    }
  };

  return (
    <div className={styles.modalContainer}>
      {step === 1 && (
        <>
          <div className={styles.inputRow}>
            <div className={styles.displayWrapper}>
              <span className={styles.displayText} onClick={() => setFocusedInput("hours")}>
                {hours}
              </span>
              <label>h</label>
              <input
                ref={hoursRef}
                id="hoursInput"
                className={styles.hiddenInput}
                type="number"
                max="23"
                value={hours}
                onFocus={() => setFocusedInput("hours")}
                onChange={(e) => handleChange(e.target.value, "hours")}
              />
            </div>
            <div className={styles.displayWrapper}>
              <span className={styles.displayText} onClick={() => setFocusedInput("minutes")}>
                {minutes}
              </span>
              <label>m</label>
              <input
                ref={minutesRef}
                id="minutesInput"
                className={styles.hiddenInput}
                type="number"
                max="59"
                value={minutes}
                onFocus={() => setFocusedInput("minutes")}
                onChange={(e) => handleChange(e.target.value, "minutes")}
              />
            </div>
            <div className={styles.displayWrapper}>
              <span className={styles.displayText} onClick={() => setFocusedInput("seconds")}>
                {seconds}
              </span>
              <label>s</label>
              <input
                ref={secondsRef}
                id="secondsInput"
                className={styles.hiddenInput}
                type="number"
                max="59"
                value={seconds}
                onFocus={() => setFocusedInput("seconds")}
                onChange={(e) => handleChange(e.target.value, "seconds")}
              />
            </div>
          </div>

          <div className={styles.keypad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button key={n} onClick={() => handleAddDigit(String(n))} className={styles.key}>
                {n}
              </button>
            ))}
            <button onClick={() => handleAddDigit("0")} className={styles.key}>
              0
            </button>
            <button onClick={() => handleAddDigit("00")} className={styles.key}>
              00
            </button>
            <button onClick={handleDelete} className={styles.deleteKey}>
              ⌫
            </button>
          </div>

          <SubmitButton
            title="play"
            primary
            onClick={startCountdown}
            btnContainerClass={styles.playContainer}
            btnClass={styles.playBtn}
          />
        </>
      )}

      {step === 2 && (
        <>
          <div className={styles.circleWrapper}>
            <svg className={styles.svg}>
              <circle cx="75" cy="75" r="65" className={styles.bgCircle} />
              <circle
                cx="75"
                cy="75"
                r="65"
                className={styles.progressCircle}
                style={{
                  strokeDashoffset: `calc(410 - (410 * ${progress}) / 100)`,
                }}
              />
            </svg>

            <div className={styles.timerText}>{formatTime(remaining)}</div>
          </div>

          <div className={styles.actions}>
            {isRunning ? (
              <SubmitButton
                title="Stop"
                onClick={stop}
                primary
                btnClass={styles.actionBtn}
              />
            ) : (
              <SubmitButton
                title="Play"
                onClick={resume}
                primary
                btnClass={styles.actionBtn}
              />
            )}

            <SubmitButton title="Cancel" onClick={reset} btnClass={styles.actionBtn} />
          </div>
        </>
      )}
    </div>
  );
};

export default CountdownModal;
